import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Building2, MapPin, Loader2, Check } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { createFacility } from '../../../../services/staffingService';

// ── Google address autocomplete ───────────────────────────────────────────────
function AddressAutocomplete({ value, onChange, onPlaceSelected }) {
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState(value || '');

  useEffect(() => { setInputValue(value || ''); }, [value]);

  useEffect(() => {
    if (!window.google?.maps?.places) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      fields: ['formatted_address', 'address_components', 'geometry'],
      componentRestrictions: { country: 'us' },
    });
    const listener = ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place.geometry) return;
      const comps = place.address_components || [];
      const data = {
        address: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        city: '', state: '', zipcode: '', country: '',
      };
      comps.forEach(c => {
        if (c.types.includes('locality')) data.city = c.long_name;
        if (c.types.includes('administrative_area_level_1')) data.state = c.short_name;
        if (c.types.includes('postal_code')) data.zipcode = c.long_name;
        if (c.types.includes('country')) data.country = c.long_name;
      });
      setInputValue(place.formatted_address);
      onPlaceSelected(data);
    });
    return () => window.google.maps.event.removeListener(listener);
  }, [onPlaceSelected]);

  return (
    <div className="relative">
      <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={e => { setInputValue(e.target.value); onChange?.(e.target.value); }}
        placeholder="Search address…"
        className="w-full rounded-lg border border-border pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background"
      />
    </div>
  );
}

// ── Map with right-click post placement ──────────────────────────────────────
function PostMap({ geoLocation, posts, onPostsChange }) {
  const mapRef      = useRef(null);
  const mapInst     = useRef(null);
  const markersRef  = useRef([]);
  const [pending, setPending]   = useState(null);
  const [note,    setNote]      = useState('');

  useEffect(() => {
    if (!window.google?.maps || !mapRef.current) return;
    const center = geoLocation?.lat
      ? { lat: geoLocation.lat, lng: geoLocation.lng }
      : { lat: 42.3601, lng: -71.0589 };

    mapInst.current = new window.google.maps.Map(mapRef.current, {
      center, zoom: 16, mapTypeId: 'satellite', disableDefaultUI: false,
    });
    mapInst.current.addListener('rightclick', e => {
      setPending({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      setNote('');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sync markers
  useEffect(() => {
    if (!mapInst.current) return;
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = posts.map((p, i) =>
      new window.google.maps.Marker({
        position: { lat: p.lat, lng: p.lng },
        map: mapInst.current,
        label: { text: String(i + 1), color: 'white', fontWeight: 'bold', fontSize: '11px' },
        title: p.note,
      })
    );
  }, [posts]);

  const addPost = () => {
    if (!note.trim()) return;
    onPostsChange([...posts, { lat: pending.lat, lng: pending.lng, note: note.trim() }]);
    setPending(null);
    setNote('');
  };

  const removePost = idx => onPostsChange(posts.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">Right-click the map to place a security checkpoint post.</p>
      <div className="relative rounded-xl overflow-hidden border border-border" style={{ height: 280 }}>
        <div ref={mapRef} className="w-full h-full" />
        {pending && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 w-64 rounded-xl bg-card border border-border shadow-lg p-3">
            <p className="text-xs font-medium text-foreground mb-2">New post</p>
            <input
              autoFocus
              value={note}
              onChange={e => setNote(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addPost()}
              placeholder="e.g. North entrance"
              className="w-full rounded-lg border border-border px-2 py-1.5 text-xs bg-background focus:outline-none focus:border-[#404880] mb-2"
            />
            <div className="flex gap-1.5">
              <button onClick={addPost}
                className="flex-1 rounded-lg bg-[#404880] py-1.5 text-xs font-medium text-white">Add</button>
              <button onClick={() => setPending(null)}
                className="flex-1 rounded-lg border border-border py-1.5 text-xs text-muted-foreground">Cancel</button>
            </div>
          </div>
        )}
      </div>
      {posts.length > 0 && (
        <div className="space-y-1">
          {posts.map((p, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-xs">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#404880] text-[10px] font-bold text-white flex-shrink-0">
                {i + 1}
              </span>
              <span className="flex-1 truncate text-foreground">{p.note}</span>
              <button onClick={() => removePost(i)} className="text-muted-foreground hover:text-red-500">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const FACILITY_TYPES = [
  'Office Building', 'Warehouse', 'Retail', 'Hospital', 'School',
  'Construction Site', 'Residential', 'Event Venue', 'Other',
];

const STEPS = ['Details', 'Location & Posts', 'Review'];

const emptyForm = () => ({
  name: '', notes: '', status: 'active', facilityType: '',
  address: '', city: '', state: '', zipcode: '', country: '',
  geoLocation: null, latitude: 0, longitude: 0,
  siteContact: { name: '', phone: '', email: '' },
  posts: [],
  documentFile: null,
});

/**
 * NewFacilitySlideOver — right-side slide-over, 3-step facility creation.
 * Props: open, onClose(), onCreated(facility), customerCompanyId
 */
const NewFacilitySlideOver = ({ open, onClose, onCreated, customerCompanyId }) => {
  const [step,      setStep]      = useState(0);
  const [form,      setForm]      = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [done,      setDone]      = useState(false);

  // Reset when re-opened
  useEffect(() => {
    if (open) { setStep(0); setForm(emptyForm()); setDone(false); }
  }, [open]);

  const set   = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setSC = (k, v) => setForm(p => ({ ...p, siteContact: { ...p.siteContact, [k]: v } }));

  const handleAddressSelected = (data) => {
    setForm(p => ({
      ...p,
      address: data.address, city: data.city, state: data.state,
      zipcode: data.zipcode, country: data.country,
      geoLocation: { lat: data.latitude, lng: data.longitude },
      latitude: data.latitude, longitude: data.longitude,
    }));
  };

  const validateStep = () => {
    if (step === 0 && !form.name.trim()) { toast.error('Facility name is required'); return false; }
    if (step === 1 && !form.address.trim()) { toast.error('Address is required'); return false; }
    return true;
  };

  const handleNext = () => { if (validateStep()) setStep(s => s + 1); };
  const handleBack = () => setStep(s => s - 1);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name',         form.name);
      fd.append('notes',        form.notes);
      fd.append('status',       form.status);
      fd.append('facilityType', form.facilityType);
      fd.append('address',      form.address);
      fd.append('city',         form.city);
      fd.append('state',        form.state);
      fd.append('zipcode',      form.zipcode);
      fd.append('country',      form.country);
      if (form.geoLocation) {
        fd.append('geoLocation[lat]', form.geoLocation.lat);
        fd.append('geoLocation[lng]', form.geoLocation.lng);
      }
      fd.append('siteContact[name]',  form.siteContact.name);
      fd.append('siteContact[phone]', form.siteContact.phone);
      fd.append('siteContact[email]', form.siteContact.email);
      if (form.posts.length > 0) {
        fd.append('posts', JSON.stringify(form.posts.map(p => ({
          position: { lat: p.lat, lng: p.lng },
          note: p.note,
        }))));
      }
      if (form.documentFile) {
        fd.append('documentFile', form.documentFile);
      }

      const resp = await createFacility(customerCompanyId, fd);
      setDone(true);
      onCreated?.(resp.data.facility);
    } catch {
      toast.error('Failed to create facility');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/40"
          />

          {/* Panel */}
          <motion.div
            key="panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            className="fixed right-0 top-0 bottom-0 z-50 flex w-full max-w-2xl flex-col bg-card shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4 flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#404880]/10">
                  <Building2 size={16} className="text-[#404880]" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground">
                    {done ? 'Facility Created!' : 'New Facility'}
                  </h2>
                  {!done && (
                    <p className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>
                  )}
                </div>
              </div>
              <button onClick={onClose}
                className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted transition-colors">
                <X size={16} />
              </button>
            </div>

            {/* Step indicator */}
            {!done && (
              <div className="flex items-center gap-2 px-6 py-3 border-b border-border flex-shrink-0">
                {STEPS.map((label, i) => (
                  <React.Fragment key={i}>
                    <div className={`flex items-center gap-1.5 text-xs font-medium ${
                      i === step ? 'text-[#404880]' : i < step ? 'text-emerald-600' : 'text-muted-foreground'
                    }`}>
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                        i < step ? 'bg-emerald-100 text-emerald-700' :
                        i === step ? 'bg-[#404880] text-white' : 'bg-muted text-muted-foreground'
                      }`}>
                        {i < step ? <Check size={10} /> : i + 1}
                      </span>
                      {label}
                    </div>
                    {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {done ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 mb-4">
                    <Check size={28} className="text-emerald-600" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-1">
                    {form.name} has been created
                  </h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    You can now create work orders for this facility.
                  </p>
                  <button onClick={onClose}
                    className="rounded-lg bg-[#404880] px-5 py-2 text-sm font-medium text-white hover:opacity-90">
                    Done
                  </button>
                </div>
              ) : (
                <>
                  {/* ── Step 0: Details ── */}
                  {step === 0 && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="block text-xs text-muted-foreground mb-1">
                            Facility Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            value={form.name}
                            onChange={e => set('name', e.target.value)}
                            placeholder="e.g. Downtown Office"
                            className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Facility Type</label>
                          <select value={form.facilityType} onChange={e => set('facilityType', e.target.value)}
                            className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background">
                            <option value="">Select type…</option>
                            {FACILITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Status</label>
                          <select value={form.status} onChange={e => set('status', e.target.value)}
                            className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background">
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs text-muted-foreground mb-1">Notes</label>
                          <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                            rows={3} placeholder="Any additional notes about this facility…"
                            className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background resize-none" />
                        </div>
                      </div>

                      <div className="border-t border-border pt-4">
                        <p className="text-xs font-semibold text-foreground mb-3">Site Contact</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <label className="block text-xs text-muted-foreground mb-1">Name</label>
                            <input value={form.siteContact.name} onChange={e => setSC('name', e.target.value)}
                              placeholder="Contact person"
                              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background" />
                          </div>
                          <div>
                            <label className="block text-xs text-muted-foreground mb-1">Phone</label>
                            <input type="tel" value={form.siteContact.phone} onChange={e => setSC('phone', e.target.value)}
                              placeholder="+1 (555) 000-0000"
                              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background" />
                          </div>
                          <div>
                            <label className="block text-xs text-muted-foreground mb-1">Email</label>
                            <input type="email" value={form.siteContact.email} onChange={e => setSC('email', e.target.value)}
                              placeholder="contact@example.com"
                              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Step 1: Location & Posts ── */}
                  {step === 1 && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">
                          Address <span className="text-red-500">*</span>
                          <span className="ml-1 text-muted-foreground/60">(select from Google suggestions)</span>
                        </label>
                        <AddressAutocomplete
                          value={form.address}
                          onPlaceSelected={handleAddressSelected}
                        />
                        {form.geoLocation?.lat && (
                          <p className="mt-1 flex items-center gap-1 text-[11px] text-emerald-600">
                            <MapPin size={10} /> Coordinates captured — {form.city}, {form.state} {form.zipcode}
                          </p>
                        )}
                      </div>

                      {form.geoLocation?.lat && (
                        <PostMap
                          geoLocation={form.geoLocation}
                          posts={form.posts}
                          onPostsChange={posts => set('posts', posts)}
                        />
                      )}

                      {!form.geoLocation?.lat && (
                        <div className="rounded-xl border border-dashed border-border bg-muted/30 p-8 text-center">
                          <MapPin size={24} className="mx-auto mb-2 text-muted-foreground opacity-40" />
                          <p className="text-sm text-muted-foreground">Select an address above to load the map</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Step 2: Review ── */}
                  {step === 2 && (
                    <div className="space-y-4">
                      <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2 text-sm">
                        <p className="font-semibold text-foreground">{form.name}</p>
                        {form.facilityType && <p className="text-xs text-muted-foreground">{form.facilityType}</p>}
                        <p className="text-sm text-foreground">{form.address}</p>
                        {(form.city || form.state) && (
                          <p className="text-xs text-muted-foreground">{[form.city, form.state, form.zipcode].filter(Boolean).join(', ')}</p>
                        )}
                        {form.siteContact.name && (
                          <p className="text-xs text-muted-foreground">Contact: {form.siteContact.name} · {form.siteContact.phone}</p>
                        )}
                        {form.notes && (
                          <p className="text-xs text-muted-foreground italic">"{form.notes}"</p>
                        )}
                      </div>

                      {form.posts.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-foreground mb-2">{form.posts.length} security post{form.posts.length !== 1 ? 's' : ''}</p>
                          {form.posts.map((p, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#404880] text-[9px] font-bold text-white">{i + 1}</span>
                              {p.note}
                            </div>
                          ))}
                        </div>
                      )}

                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">Layout Plan / Document (optional)</label>
                        {form.documentFile ? (
                          <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
                            <span className="flex-1 text-xs text-foreground truncate">{form.documentFile.name}</span>
                            <button onClick={() => set('documentFile', null)} className="text-muted-foreground hover:text-red-500">
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-3 hover:bg-muted/50 transition-colors">
                            <input type="file" className="hidden" accept=".pdf,.png,.jpg"
                              onChange={e => set('documentFile', e.target.files?.[0] || null)} />
                            <span className="text-xs text-muted-foreground">Click to upload a PDF or image</span>
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            {!done && (
              <div className="flex items-center justify-between border-t border-border px-6 py-4 flex-shrink-0">
                {step > 0 ? (
                  <button onClick={handleBack}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
                    <ChevronLeft size={16} /> Back
                  </button>
                ) : (
                  <button onClick={onClose}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Cancel
                  </button>
                )}

                {step < STEPS.length - 1 ? (
                  <button onClick={handleNext}
                    className="flex items-center gap-1 rounded-lg bg-[#404880] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity">
                    Next <ChevronRight size={16} />
                  </button>
                ) : (
                  <button onClick={handleSubmit} disabled={submitting}
                    className="flex items-center gap-1.5 rounded-lg bg-[#CC457D] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 transition-opacity">
                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    {submitting ? 'Creating…' : 'Create Facility'}
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NewFacilitySlideOver;
